import React, { useRef, useEffect, useReducer, useState } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@foris/avocado-suite";
import { Icon } from "@foris/avocado-icons";
import DropFilter from "./DropFilter/DropFilter";
import { IDropFilterProps } from "./DropFilter/DropFilter";
import Options, { IOption } from "./Options";
import cx from "classnames";
import useClickOutside from "@common/hooks/useClickOutside";
import styles from "./autocomplete.module.scss";

export interface IAutocompleteInput {
  onChange?: (value: string) => void;
  onSelect?: (value: IOption) => void;
  onEnter?: (value: string) => void;
  value?: string;
  options?: Array<IOption>;
  placeholder?: string;
  loading?: boolean;
  disabled?: boolean;
}

interface IAutocompleteProps {
  filter?: IDropFilterProps;
  input: IAutocompleteInput;
  className?: string;
  onShowOptions?: (show: boolean) => void;
}

interface IAutocompleteState {
  options: Array<IOption>;
  inputValue: string;
  showOptions: boolean;
}

const stateReducer = (current: IAutocompleteState, updated: Partial<IAutocompleteState>) => ({
  ...current,
  ...updated,
});

const Autocomplete: React.FC<IAutocompleteProps> = ({
  filter,
  input,
  className = "",
  onShowOptions,
}) => {
  const [state, setState] = useReducer(stateReducer, {
    inputValue: input.value || "",
    options: input.options,
    showOptions: false,
  });

  const [showTooltip, setShowTooltip] = useState(false);
  const showOptions = !input.loading && state.showOptions && !!state.inputValue;

  const ref = useRef(null);
  useClickOutside({ ref, onClickOutside: () => setState({ showOptions: false }) });
  const { t } = useTranslation();

  const handleSelect = (value: IOption | null) => {
    const { inputValue } = state;
    const valueInput = value ? value.label : inputValue;

    setState({ options: [], inputValue: valueInput.toString(), showOptions: false });

    if (value && value.value) {
      input.onSelect(value);
    }
  };

  const handleChangeInput = (valueInp: React.ChangeEvent<HTMLInputElement>) => {
    const valueChange = valueInp.currentTarget.value;

    setState({ inputValue: valueChange });
    input.onChange(valueChange);
  };

  useEffect(() => {
    const valueOptions = input.options;
    let showOpt = valueOptions && valueOptions.length > 0;

    if (showOpt && !state.inputValue) {
      showOpt = false;
    }

    setState({
      options: valueOptions,
      showOptions: showOpt,
      inputValue: input.value,
    });
  }, [input.options, state.inputValue, showTooltip]);

  useEffect(() => {
    onShowOptions?.(showOptions);
  }, [showOptions]);

  const visibilityTooltip = (show: boolean) => {
    if (show) {
      setShowTooltip(filter?.value === t("header.dimension.title"));
    } else {
      setShowTooltip(false);
    }
  };

  return (
    <section className={cx(styles.cntAutocomplete, className)}>
      {filter && (
        <DropFilter
          key={filter?.value}
          options={filter.options}
          onSelect={value => {
            setState({ options: [] });
            filter.onSelect(value);
            setShowTooltip(false);
          }}
          value={filter.value || null}
          onCallback={() => setState({ showOptions: false })}
        />
      )}

      <Tooltip
        label={t("home.action-cards.editing-schedules.search.tooltip-label")}
        placement="bottomLeft"
        visible={showTooltip}
      >
        <div
          onMouseEnter={() => visibilityTooltip(true)}
          onMouseLeave={() => visibilityTooltip(false)}
          className={cx(
            styles.cntAutocomplete_input,
            input.disabled && styles.cntAutocomplete_input__disabled,
            !!filter && styles.cntAutocomplete_input__withFilter,
          )}
          ref={ref}
        >
          <input
            type="text"
            disabled={input.disabled}
            onMouseEnter={() => visibilityTooltip(true)}
            onMouseLeave={() => visibilityTooltip(false)}
            className={styles.inputAutocomplete}
            placeholder={input.placeholder || ""}
            onChange={handleChangeInput}
            onKeyUp={event => {
              if (event.key === "Enter" && input.onEnter) {
                input.onEnter(state.inputValue);
              }
            }}
            value={state.inputValue}
          />

          {input.loading && <Options loading={input.loading} options={[]} />}
          {showOptions && <Options options={state.options} onSelect={handleSelect} />}
        </div>
      </Tooltip>
      <button
        className={styles.cntAutocomplete_btn}
        onClick={() => input?.onEnter?.(state.inputValue)}
      >
        <Icon name="search" size="md" color="neutral-00" />
      </button>
    </section>
  );
};

export default Autocomplete;
